import { supabase } from '../lib/supabase';

/**
 * Plan key → human-readable label used on the pricing UI.
 * The key matches what the stripe-billing edge function expects as `planKey`.
 */
export const PLAN_KEYS = {
  PRO_MONTHLY: 'pro-monthly',
  PRO_PLUS_MONTHLY: 'pro-plus-monthly',
};

/**
 * Service for Stripe billing operations.
 * All Stripe API calls are proxied through the stripe-billing Supabase Edge Function
 * so no Stripe secret key is ever exposed in the browser.
 */
class StripeService {
  /**
   * Call the stripe-billing Supabase Edge Function.
   * @param {object} payload - Action payload.
   * @returns {Promise<object>} Parsed response from the edge function.
   */
  async #call(payload) {
    const { data, error } = await supabase.functions.invoke('stripe-billing', {
      body: payload,
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  }

  /**
   * Get billing status for an account.
   * Returns the current plan_name (from memberships) and whether a Stripe
   * subscription is attached.
   * @param {string} accountId - Supabase account identifier.
   * @returns {Promise<{plan_name: string, has_subscription: boolean, stripe_customer_id: string|null}>}
   */
  async getBillingStatus(accountId) {
    return this.#call({ action: 'get-billing-status', accountId });
  }

  /**
   * Create a Stripe Checkout session and redirect the browser to it.
   * Used when upgrading from Free to a paid plan.
   * @param {string} accountId - Supabase account identifier.
   * Seats are billed per active member: the server starts checkout at the account's current
   * member count, and later invites and removals update it (prorated on the next invoice).
   * @param {string} planKey - Plan key, e.g. PLAN_KEYS.PRO_MONTHLY.
   * @returns {Promise<void>} Redirects browser to Stripe Checkout.
   */
  async startCheckout(accountId, planKey) {
    const { url } = await this.#call({
      action: 'create-checkout-session',
      accountId,
      planKey,
    });
    window.location.href = url;
  }


  /**
   * Create a Stripe Customer Portal session and redirect the browser to it.
   * The portal handles plan changes, payment methods, invoices and cancellations. Seat counts
   * follow the team's active members and are not edited in the portal.
   * Stripe fires webhooks for every change; the stripe-webhook function keeps
   * memberships.plan_name in sync automatically.
   * @param {string} accountId - Supabase account identifier.
   * @returns {Promise<void>} Redirects browser to Stripe Customer Portal.
   */
  async openBillingPortal(accountId) {
    const { url } = await this.#call({
      action: 'create-portal-session',
      accountId,
    });
    window.location.href = url;
  }
}

const stripeService = new StripeService();
export default stripeService;
